import axios, { AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'

import {
  ApiError,
  ApiErrorCode,
  isSuccessEnvelope,
  type ApiEnvelope,
  type RequestMeta,
  type ValidationErrors,
} from '@/types/api'
import type { MaintenanceState } from '@/types/admin'
import type { AuthTokens, LoginResult } from '@/types/user'
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from './tokenStorage'

declare module 'axios' {
  // Lets callers pass `{ meta: { silent: true } }` to any request.
  export interface AxiosRequestConfig {
    meta?: RequestMeta
  }
}

/** Config carrying our single-retry marker for the refresh flow. */
type RetryableConfig = InternalAxiosRequestConfig & { retried?: boolean }

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

export const http = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20_000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  // Bearer-token auth: the backend returns the tokens in the JSON body, so there
  // is no cookie to send. Not an oversight — switching to cookie auth would mean
  // building CSRF protection for `api/*` (there is none today) and would break the
  // Netlify deploy previews, which are cross-site with respect to the API domain.
  // The XSS exposure that comes with it is mitigated by the CSP (see netlify.toml).
  withCredentials: false,
})

/* -------------------------------------------------------------------------- */
/* Hooks wired up in main.ts — keeps this module free of store/router imports  */
/* -------------------------------------------------------------------------- */

type SessionExpiredHandler = () => void | Promise<void>
type ErrorNotifier = (error: ApiError) => void
type MaintenanceHandler = (state: MaintenanceState | null) => void | Promise<void>

let sessionExpiredHandler: SessionExpiredHandler | null = null
let errorNotifier: ErrorNotifier | null = null
let maintenanceHandler: MaintenanceHandler | null = null

/** Called once the refresh flow has definitively failed. */
export function setSessionExpiredHandler(handler: SessionExpiredHandler | null): void {
  sessionExpiredHandler = handler
}

/** Called for every failed request that didn't opt out via `meta.silent`. */
export function setErrorNotifier(notifier: ErrorNotifier | null): void {
  errorNotifier = notifier
}

/**
 * Called when the API answers 503, i.e. an admin closed the site while this
 * session was already running. The body carries the maintenance state, so the
 * app can switch over without waiting for the next status poll.
 */
export function setMaintenanceHandler(handler: MaintenanceHandler | null): void {
  maintenanceHandler = handler
}

/** Reads the `maintenance` block the 503 body carries, if it is there. */
function extractMaintenanceState(data: unknown): MaintenanceState | null {
  if (!data || typeof data !== 'object') return null
  const state = (data as { maintenance?: unknown }).maintenance
  if (!state || typeof state !== 'object') return null
  return state as MaintenanceState
}

function notify(config: RetryableConfig | undefined, error: ApiError): void {
  if (config?.meta?.silent) return
  errorNotifier?.(error)
}

/* -------------------------------------------------------------------------- */
/* Token refresh (single-flight)                                              */
/* -------------------------------------------------------------------------- */

let refreshInFlight: Promise<AuthTokens> | null = null

/**
 * Exchanges the refresh token for a new pair.
 *
 * Uses a bare axios call on purpose: going through `http` would re-enter these
 * interceptors and could recurse. Note the backend authenticates this endpoint
 * with the **refresh** token in the Authorization header, not the access token —
 * an access token is rejected with 403 (`abilities:issue-access-token`).
 *
 * The refresh token is **single-use**: the backend revokes the one that was spent,
 * so the pair returned here must replace the stored one before anything else uses it.
 */
async function performRefresh(): Promise<AuthTokens> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    throw new ApiError('Missing refresh token', {
      status: 401,
      translationKey: 'errors.sessionExpired',
    })
  }

  const response = await axios.post<ApiEnvelope<LoginResult>>(
    `${API_BASE_URL}/auth/refresh-token`,
    {},
    {
      headers: { Accept: 'application/json', Authorization: `Bearer ${refreshToken}` },
      timeout: 20_000,
    },
  )

  const body = response.data
  // Remember: this endpoint reports failures with HTTP 200 + error != 0.
  if (!body || !isSuccessEnvelope(body)) {
    throw new ApiError(body?.message || 'Refresh failed', {
      code: body?.error ?? null,
      status: response.status,
      translationKey: 'errors.sessionExpired',
    })
  }

  const tokens: AuthTokens = {
    accessToken: body.result.accessToken,
    refreshToken: body.result.refreshToken,
  }
  setTokens(tokens)
  return tokens
}

/**
 * Guarantees a single concurrent refresh. This is not just an optimisation: the
 * backend rotates the refresh token, revoking the one it was handed. Two parallel
 * refreshes would therefore send the same token twice and the second would come
 * back 401 — logging the user out mid-session. Every request that hit a 401 waits
 * on this one promise instead.
 */
function refreshOnce(): Promise<AuthTokens> {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

/* -------------------------------------------------------------------------- */
/* Interceptors                                                               */
/* -------------------------------------------------------------------------- */

http.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

/**
 * Tells an expired/invalid token apart from an authorization denial. Both come
 * back as HTTP 401.
 *
 * The backend funnels *every* API exception through a single handler in
 * `bootstrap/app.php`, which rewrites it as `{error: GENERIC_ERROR (1), message}`.
 * So an expired Sanctum token arrives as `{"error":1,"message":"Unauthenticated."}`
 * — **not** as Laravel's bare `{"message":"Unauthenticated."}`.
 *
 * The only 401 that must not trigger a refresh is the authorization denial from
 * `BaseController::notAuthorized()`, which uses NOT_AUTHORIZED (3): retrying it
 * with a brand-new token would fail identically.
 */
function isExpiredTokenResponse(response: AxiosResponse): boolean {
  if (response.status !== 401) return false
  const data: unknown = response.data
  const code = data && typeof data === 'object' ? (data as { error?: unknown }).error : undefined
  return code !== ApiErrorCode.NOT_AUTHORIZED
}

function extractValidationErrors(data: unknown): ValidationErrors {
  if (!data || typeof data !== 'object') return {}
  const errors = (data as { errors?: unknown }).errors
  if (!errors || typeof errors !== 'object') return {}
  return errors as ValidationErrors
}

/** Turns any axios failure into a predictable ApiError. */
function toApiError(error: AxiosError): ApiError {
  if (!error.response) {
    const timedOut = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT'
    return new ApiError(error.message || 'Network error', {
      translationKey: timedOut ? 'errors.timeout' : 'errors.network',
    })
  }

  const { status, data } = error.response
  const message =
    (data && typeof data === 'object' && typeof (data as { message?: unknown }).message === 'string'
      ? (data as { message: string }).message
      : null) ?? error.message

  const code =
    data && typeof data === 'object' && typeof (data as { error?: unknown }).error === 'number'
      ? (data as { error: number }).error
      : null

  return new ApiError(message, {
    code,
    status,
    fieldErrors: status === 422 ? extractValidationErrors(data) : {},
    translationKey: status >= 500 ? 'errors.server' : undefined,
  })
}

http.interceptors.response.use(
  // --- success transport, which may still carry a business error -------------
  (response) => {
    const body: unknown = response.data

    if (body && typeof body === 'object' && 'error' in body) {
      const envelope = body as ApiEnvelope<unknown>

      if (envelope.error !== 0) {
        // Le chiavi in più della busta (vedi `errorResponseData` lato backend)
        // arrivano al chiamante: servono ai casi in cui deve *decidere* su un
        // numero — es. quanti villaggi cancellerebbe una dissociazione — e non
        // solo mostrare la frase.
        const {
          error: _code,
          message: _message,
          ...extra
        } = body as unknown as Record<string, unknown>
        const apiError = new ApiError(
          ('message' in envelope && envelope.message) || 'Unexpected error',
          { code: envelope.error, status: response.status, data: extra },
        )
        // Thrown from a fulfilled handler, so our own onRejected below never
        // sees it: notify here instead.
        notify(response.config as RetryableConfig, apiError)
        throw apiError
      }

      // Hand callers the payload directly instead of the envelope.
      response.data = (envelope as { result: unknown }).result
    }

    return response
  },

  // --- transport-level failures --------------------------------------------
  async (error: unknown) => {
    if (error instanceof ApiError) throw error

    if (!axios.isAxiosError(error)) {
      const unknownError = new ApiError('Unexpected error', {
        translationKey: 'errors.unexpected',
      })
      notify(undefined, unknownError)
      throw unknownError
    }

    const config = error.config as RetryableConfig | undefined
    const response = error.response

    // The site was closed while this session was running: hand the state over
    // so the app can show the notice now, instead of at the next status poll.
    if (response?.status === 503) {
      await maintenanceHandler?.(extractMaintenanceState(response.data))
    }

    const canRefresh =
      response !== undefined &&
      config !== undefined &&
      !config.retried &&
      !config.meta?.skipAuthRefresh &&
      isExpiredTokenResponse(response)

    if (canRefresh && config) {
      config.retried = true
      try {
        const tokens = await refreshOnce()
        config.headers.Authorization = `Bearer ${tokens.accessToken}`
        // Replay the original request; concurrent 401s all await the same refresh.
        return await http.request(config)
      } catch {
        clearTokens()
        const expired = new ApiError('Session expired', {
          status: 401,
          translationKey: 'errors.sessionExpired',
        })
        notify(config, expired)
        await sessionExpiredHandler?.()
        throw expired
      }
    }

    const apiError = toApiError(error)
    notify(config, apiError)
    throw apiError
  },
)

/** Exposed for tests, which need to reset the module-level refresh state. */
export function __resetRefreshStateForTests(): void {
  refreshInFlight = null
}
